import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, getImagesUrl, failedResponse, INVALID_ID } from "../common";
import { SubjectInfo } from "./model";
import { getToken } from "../../features/user/UserAvatar";

export interface SubjectDeleteRequest {
  subjectId: string;
}
export const subjectDelete = createAsyncThunk(
  "subjectDelete",
  async (param: SubjectDeleteRequest, thunkAPI) => {
    console.debug("delete subject");
    const response = await fetch(`${getBackendUrl()}/api/subject/${param.subjectId}`, {
      method: "DELETE",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      }
    });
    if (response.ok) {
      return { subjectId: param.subjectId };
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export interface SubjectInsUpdState {
  state: LoadStateInfo;
  subject: SubjectInfo;
}

const initialState: SubjectInsUpdState = {
  state: notStarted(),
  subject: emptySubject(),
};

function emptySubject(): SubjectInfo {
  return {
    id: Number(INVALID_ID),
    name: "",
    url: `${getImagesUrl()}/default/routine.webp`,
    schoolId: Number(INVALID_ID),
  };
}

export interface SubjectInsertRequest {
  subject: SubjectInfo;
}
export const subjectInsert = createAsyncThunk(
  "subjectInsert",
  async (param: SubjectInsertRequest, thunkAPI) => {
    console.debug("insert subject");
    const response = await fetch(`${getBackendUrl()}/api/subject`, {
      method: "POST",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.subject)
    });
    if (response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export interface SubjectUpdateRequest {
  subjectId: string;
  subject: SubjectInfo;
}
export const subjectUpdate = createAsyncThunk(
  "subjectUpdate",
  async (param: SubjectUpdateRequest, thunkAPI) => {
    console.debug("update subject");
    const response = await fetch(`${getBackendUrl()}/api/subject/${param.subjectId}`, {
      method: "PUT",
      signal: thunkAPI.signal,
      headers: {
        "Authorization": `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(param.subject)
    });
    if (response.ok) {
      return await response.json();
    } else {
      return thunkAPI.rejectWithValue(failedResponse(response));
    }
  }
);

export const subjectInsUpdSlice = createSlice({
  name: "subjectInsUpd",
  initialState,
  reducers: {
    set: (state, action: PayloadAction<SubjectInfo>) => {
      state.state = notStarted();
      state.subject = action.payload;
    },
    clean: (state) => {
      state.state = notStarted();
      state.subject = emptySubject();
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(subjectInsert.pending, (state, /* action */) => {
        state.state = loading();
      })
      .addCase(subjectInsert.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.subject = action.payload;
      })
      .addCase(subjectInsert.rejected, (state, action) => {
        state.state = error(action);
      })
      .addCase(subjectUpdate.pending, (state, /* action */) => {
        state.state = loading();
      })
      .addCase(subjectUpdate.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.subject = action.payload;
      })
      .addCase(subjectUpdate.rejected, (state, action) => {
        state.state = error(action);
      })
      .addCase(subjectDelete.pending, (state, /* action */) => {
        state.state = loading();
      })
      .addCase(subjectDelete.fulfilled, (state, action) => {
        console.debug(`${action.type}`);
        state.state = loaded();
        state.subject = emptySubject();
      })
      .addCase(subjectDelete.rejected, (state, action) => {
        state.state = error(action);
      });
  },
});

// export const { } = subjectInsUpdSlice.actions;

export const selectSubjectInsUpd = (state: RootState) => state.subjectInsUpd;

export default subjectInsUpdSlice.reducer;
